import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  FormControl,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Box,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { v4 as uuidv4 } from "uuid";
import config from "../../../aws-exports";
import {
  fetchData,
  postQuery,
  uploadFileToS3Path,
} from "../../../services/BackendServices";
import AlertModal from "../../Requirement/components/common/AlerModel";
import { useLoading } from "../../../context/LoadingContext";

const SellerFarmerKYC = () => {
  const navigate = useNavigate();
  const { setLoading, loading } = useLoading();
  const [activeUser, setActiveUser] = useState("");
  const [activeUserName, setActiveUserName] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [userKycID, setUserKycID] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    fullName: "",
    aadhaarNumber: "",
    aadhaarVerified: false,
    panNumber: "",
    panVerified: false,
    bankAccountNumber: "",
    bankIfsc: "",
    bankVerified: false,
    selfieImage: null,
    remarks: "",
    Status: "",
  });

  // Webcam states
  const [webcamDialogOpen, setWebcamDialogOpen] = useState(false);
  const [webcamActive, setWebcamActive] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  // Aadhaar OTP states
  const [aadhaarModalOpen, setAadhaarModalOpen] = useState(false);
  const [aadhaarOtp, setAadhaarOtp] = useState("");
  const [aadhaarOtpSent, setAadhaarOtpSent] = useState(false);
  const [aadhaarLoading, setAadhaarLoading] = useState(false);
  const [aadhaarTransactionId, setAadhaarTransactionId] = useState("");

  // PAN modal states
  const [panModalOpen, setPanModalOpen] = useState(false);
  const [panLoading, setPanLoading] = useState(false);

  // Bank modal states
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [bankLoading, setBankLoading] = useState(false);

  // S3 config
  const bucket = process.env.REACT_APP_AMTPL_BUCKET;
  const bucketfolder = "public/media/amtpl";
  const filePath = `${bucketfolder}/`;
  const fileUrl = `https://${bucket}.s3.${config.aws_project_region}.amazonaws.com/${filePath}`;

  // Modal state
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleTabChange = (event, newValue) => setActiveTab(newValue);
  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  const readOnly =
    formData.Status === "Approved" ||
    (isUpdateMode &&
      formData.Status &&
      formData.Status !== "Approved" &&
      !editMode);

  // Check user authentication
  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        } else {
          setActiveUser(user.attributes.email);
          setActiveUserName(user.attributes.given_name || "");
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };
    checkUser();
  }, [navigate]);

  // Helper function to parse KycData string from listUserKyc
  const parseKycDataString = (kycDataStr) => {
    if (!kycDataStr) return null;
    try {
      // Parse string like "{stg_aadhaar=otp_sent, aadhaar_txnId=xxx, stg_pan=verified, stg_bank=verified}"
      const cleanStr = kycDataStr.replace(/^\{|\}$/g, ""); // Remove { and }
      const pairs = cleanStr.split(", ");
      const result = {};
      pairs.forEach((pair) => {
        const [key, value] = pair.split("=");
        if (key && value) {
          result[key.trim()] = value.trim();
        }
      });
      return {
        aadhaarVerified: result.stg_aadhaar === "verified",
        aadhaarNumber: result.aadhaar_number || "",
        aadhaarStatus: result.stg_aadhaar || "no",
        panVerified: result.stg_pan === "verified",
        panNumber: result.pan_number || "",
        panStatus: result.stg_pan || "no",
        bankVerified: result.stg_bank === "verified",
        bankAccount: result.bank_account || "",
        bankIfsc: result.bank_ifsc || "",
        bankStatus: result.stg_bank || "no",
      };
    } catch (e) {
      console.error("Error parsing KycData string:", e);
      return null;
    }
  };

  // Fetch existing KYC data from both UserKyc table and userVault
  const fetchExistingKYC = useCallback(async () => {
    if (!activeUser) return;
    setLoading(true);
    try {
      // Fetch existing KYC record from UserKyc table first
      const userKycSettings = await fetchData({
        target: "listUserKyc",
        userOrder: "UserKycID",
        rowStart: 0,
        pageSize: 1,
        filterTerms: { EmailID: activeUser },
      });

      let kycDataFromTable = null;
      let parsedKycData = null;

      if (userKycSettings.items && userKycSettings.items.length > 0) {
        kycDataFromTable = userKycSettings.items[0];
        // Parse KycData field if it exists
        if (kycDataFromTable.KycData) {
          parsedKycData = parseKycDataString(kycDataFromTable.KycData);
        }
      }

      // If we have data from listUserKyc, use it (primary source)
      if (kycDataFromTable) {
        setIsUpdateMode(true);
        setUserKycID(kycDataFromTable.UserKycID);

        // Use parsedKycData for verification status, fallback to table fields
        setFormData({
          fullName: kycDataFromTable.FullName || "",
          aadhaarNumber:
            parsedKycData?.aadhaarNumber ||
            kycDataFromTable.AadhaarNumber ||
            "",
          aadhaarVerified:
            parsedKycData?.aadhaarVerified ||
            kycDataFromTable.AadhaarVerified === "true" ||
            kycDataFromTable.AadhaarVerified === true,
          panNumber:
            parsedKycData?.panNumber || kycDataFromTable.PanNumber || "",
          panVerified:
            parsedKycData?.panVerified ||
            kycDataFromTable.PanVerified === "true" ||
            kycDataFromTable.PanVerified === true,
          bankAccountNumber:
            parsedKycData?.bankAccount ||
            kycDataFromTable.BankAccountNumber ||
            "",
          bankIfsc: parsedKycData?.bankIfsc || kycDataFromTable.BankIfsc || "",
          bankVerified:
            parsedKycData?.bankVerified ||
            kycDataFromTable.BankVerified === "true" ||
            kycDataFromTable.BankVerified === true,
          selfieImage: kycDataFromTable.LiveSelfiePhoto
            ? { name: kycDataFromTable.LiveSelfiePhoto }
            : null,
          remarks: kycDataFromTable.Remarks || "",
          Status: kycDataFromTable.Status || "",
        });
        if (kycDataFromTable.LiveSelfiePhoto) {
          setCapturedImage(`${fileUrl}${kycDataFromTable.LiveSelfiePhoto}`);
        }
        setSubmitted(kycDataFromTable.Status !== "Pending");
        setEditMode(false);
      } else {
        // No data from listUserKyc, try getKycData from userVault as fallback
        try {
          const vaultResponse = await postQuery({
            target: "getKycData",
            emailId: activeUser,
          });

          let vaultKycData = null;
          if (
            vaultResponse &&
            vaultResponse.items &&
            vaultResponse.items.success
          ) {
            vaultKycData = vaultResponse.items.data;
          }

          if (vaultKycData) {
            // Have vault data - populate from vault
            setIsUpdateMode(false);
            setUserKycID(null);
            setFormData((prev) => ({
              ...prev,
              aadhaarNumber: vaultKycData.aadhaarNumber || "",
              aadhaarVerified: vaultKycData.aadhaarVerified || false,
              panNumber: vaultKycData.panNumber || "",
              panVerified: vaultKycData.panVerified || false,
              bankAccountNumber: vaultKycData.bankAccount || "",
              bankIfsc: vaultKycData.bankIfsc || "",
              bankVerified: vaultKycData.bankVerified || false,
            }));
            setEditMode(false);
          } else {
            setIsUpdateMode(false);
            setUserKycID(null);
            setEditMode(false);
          }
        } catch (vaultError) {
          console.error("Error fetching vault KYC data:", vaultError);
          setIsUpdateMode(false);
          setUserKycID(null);
          setEditMode(false);
        }
      }
    } catch (error) {
      console.error("Error fetching KYC data:", error);
    } finally {
      setLoading(false);
    }
  }, [activeUser, fileUrl, setLoading]);

  useEffect(() => {
    if (activeUser) {
      fetchExistingKYC();
    }
  }, [activeUser, fetchExistingKYC]);

  // Webcam functions
  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setWebcamActive(true);
      }
    } catch (err) {
      console.error("Error accessing webcam:", err);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message:
          "Failed to access webcam. Please ensure permissions are granted.",
        autoClose: false,
      });
      setWebcamDialogOpen(false);
    }
  };

  const stopWebcam = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach((track) => track.stop());
      videoRef.current.srcObject = null;
    }
    setWebcamActive(false);
  };

  const capturePhoto = () => {
    const context = canvasRef.current.getContext("2d");
    context.drawImage(videoRef.current, 0, 0, 640, 480);
    canvasRef.current.toBlob((blob) => {
      const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
      const imageUrl = URL.createObjectURL(blob);
      setFormData((prev) => ({ ...prev, selfieImage: file }));
      setCapturedImage(imageUrl);
      stopWebcam();
      setWebcamDialogOpen(false);
    }, "image/jpeg");
  };

  useEffect(() => {
    if (webcamDialogOpen && !webcamActive) {
      startWebcam();
    }
    return () => stopWebcam();
  }, [webcamDialogOpen]);

  const removeSelfie = () => {
    if (readOnly) return;
    setFormData((prev) => ({ ...prev, selfieImage: null }));
    setCapturedImage(null);
  };

  // Aadhaar verification handlers
  const handleAadhaarModalOpen = () => {
    setAadhaarModalOpen(true);
    setAadhaarOtp("");
    setAadhaarOtpSent(false);
    setAadhaarTransactionId("");
  };

  const handleAadhaarModalClose = () => {
    setAadhaarModalOpen(false);
    setAadhaarOtp("");
    setAadhaarOtpSent(false);
    setAadhaarLoading(false);
    setAadhaarTransactionId("");
  };

  const handleAadhaarSendOtp = async () => {
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      setModal({
        show: true,
        type: "error",
        title: "Full Name Required",
        message: "Please enter your full name before verifying Aadhaar.",
        autoClose: false,
      });
      return;
    }

    if (!formData.aadhaarNumber || formData.aadhaarNumber.length !== 12) {
      setModal({
        show: true,
        type: "error",
        title: "Invalid Aadhaar",
        message: "Please enter a valid 12-digit Aadhaar number.",
        autoClose: false,
      });
      return;
    }

    setAadhaarLoading(true);
    try {
      const response = await postQuery({
        target: "verifyAadhaarSendOtp",
        aadhaarNumber: formData.aadhaarNumber,
        emailId: activeUser,
        fullName: formData.fullName.trim(),
      });

      if (response && response.items && response.items.success) {
        setAadhaarOtpSent(true);
        setAadhaarTransactionId(response.items.transactionId || "");
        setModal({
          show: true,
          type: "success",
          title: "OTP Sent",
          message: "OTP has been sent to your Aadhaar-linked mobile number.",
          autoClose: true,
        });
      } else {
        setModal({
          show: true,
          type: "error",
          title: "Error",
          message:
            response?.items?.message || "Failed to send OTP. Please try again.",
          autoClose: false,
        });
      }
    } catch (error) {
      console.error("Aadhaar OTP error:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Failed to send OTP. Please try again.",
        autoClose: false,
      });
    } finally {
      setAadhaarLoading(false);
    }
  };

  const handleAadhaarOtpVerify = async () => {
    if (!aadhaarOtp || aadhaarOtp.length !== 6) {
      setModal({
        show: true,
        type: "error",
        title: "Invalid OTP",
        message: "Please enter a valid 6-digit OTP.",
        autoClose: false,
      });
      return;
    }

    setAadhaarLoading(true);
    try {
      const response = await postQuery({
        target: "verifyAadhaarOtp",
        aadhaarNumber: formData.aadhaarNumber,
        otp: aadhaarOtp,
        transactionId: aadhaarTransactionId,
        emailId: activeUser,
      });

      if (response && response.items && response.items.success) {
        setFormData((prev) => ({ ...prev, aadhaarVerified: true }));
        handleAadhaarModalClose();
        setModal({
          show: true,
          type: "success",
          title: "Verification Successful",
          message: "Your Aadhaar has been verified successfully.",
          autoClose: false,
        });
      } else {
        setModal({
          show: true,
          type: "error",
          title: "Verification Failed",
          message:
            response?.items?.message ||
            "OTP verification failed. Please try again.",
          autoClose: false,
        });
      }
    } catch (error) {
      console.error("Aadhaar OTP verify error:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Verification failed. Please try again.",
        autoClose: false,
      });
    } finally {
      setAadhaarLoading(false);
    }
  };

  // PAN verification handlers
  const handlePanModalOpen = () => {
    setPanModalOpen(true);
  };

  const handlePanModalClose = () => {
    setPanModalOpen(false);
    setPanLoading(false);
  };

  const handlePanVerify = async () => {
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      setModal({
        show: true,
        type: "error",
        title: "Full Name Required",
        message: "Please enter your full name before verifying PAN.",
        autoClose: false,
      });
      return;
    }

    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (
      !formData.panNumber ||
      !panRegex.test(formData.panNumber.toUpperCase())
    ) {
      setModal({
        show: true,
        type: "error",
        title: "Invalid PAN",
        message: "Please enter a valid PAN number (e.g., ABCDE1234F).",
        autoClose: false,
      });
      return;
    }

    setPanLoading(true);
    try {
      const response = await postQuery({
        target: "verifyPan",
        panNumber: formData.panNumber.toUpperCase(),
        fullName: formData.fullName.trim(),
        emailId: activeUser,
      });

      if (response && response.items && response.items.success) {
        setFormData((prev) => ({ ...prev, panVerified: true }));
        handlePanModalClose();
        setModal({
          show: true,
          type: "success",
          title: "Verification Successful",
          message: "Your PAN has been verified successfully.",
          autoClose: false,
        });
      } else {
        setModal({
          show: true,
          type: "error",
          title: "Verification Failed",
          message:
            response?.items?.message ||
            "PAN verification failed. Please try again.",
          autoClose: false,
        });
      }
    } catch (error) {
      console.error("PAN verify error:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Verification failed. Please try again.",
        autoClose: false,
      });
    } finally {
      setPanLoading(false);
    }
  };

  // Bank verification handlers
  const handleBankModalOpen = () => {
    setBankModalOpen(true);
  };

  const handleBankModalClose = () => {
    setBankModalOpen(false);
    setBankLoading(false);
  };

  const handleBankVerify = async () => {
    if (!formData.fullName || formData.fullName.trim().length < 2) {
      setModal({
        show: true,
        type: "error",
        title: "Full Name Required",
        message: "Please enter your full name before verifying Bank.",
        autoClose: false,
      });
      return;
    }

    if (!formData.bankAccountNumber || formData.bankAccountNumber.length < 9) {
      setModal({
        show: true,
        type: "error",
        title: "Invalid Account Number",
        message: "Please enter a valid bank account number.",
        autoClose: false,
      });
      return;
    }

    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (
      !formData.bankIfsc ||
      !ifscRegex.test(formData.bankIfsc.toUpperCase())
    ) {
      setModal({
        show: true,
        type: "error",
        title: "Invalid IFSC",
        message: "Please enter a valid IFSC code (e.g., SBIN0001234).",
        autoClose: false,
      });
      return;
    }

    setBankLoading(true);
    try {
      const response = await postQuery({
        target: "verifyBankAccount",
        bankAccountNumber: formData.bankAccountNumber,
        ifscCode: formData.bankIfsc.toUpperCase(),
        emailId: activeUser,
        fullName: formData.fullName.trim(),
      });

      if (response && response.items && response.items.success) {
        setFormData((prev) => ({ ...prev, bankVerified: true }));
        handleBankModalClose();
        setModal({
          show: true,
          type: "success",
          title: "Verification Successful",
          message: "Your bank account has been verified successfully.",
          autoClose: false,
        });
      } else {
        setModal({
          show: true,
          type: "error",
          title: "Verification Failed",
          message:
            response?.items?.message ||
            "Bank verification failed. Please try again.",
          autoClose: false,
        });
      }
    } catch (error) {
      console.error("Bank verify error:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Verification failed. Please try again.",
        autoClose: false,
      });
    } finally {
      setBankLoading(false);
    }
  };

  // Input change handlers to prevent focus loss
  const handleAadhaarNumberChange = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 12);
    setFormData((prev) => ({ ...prev, aadhaarNumber: value }));
  }, []);

  const handlePanNumberChange = useCallback((e) => {
    const value = e.target.value.toUpperCase().slice(0, 10);
    setFormData((prev) => ({ ...prev, panNumber: value }));
  }, []);

  const handleBankAccountChange = useCallback((e) => {
    const value = e.target.value.replace(/\D/g, "");
    setFormData((prev) => ({ ...prev, bankAccountNumber: value }));
  }, []);

  const handleBankIfscChange = useCallback((e) => {
    const value = e.target.value.toUpperCase().slice(0, 11);
    setFormData((prev) => ({ ...prev, bankIfsc: value }));
  }, []);

  // Submit handler
  const handleSubmit = async () => {
    if (readOnly) return;
    if (!activeUser) {
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "User email not available. Please log in again.",
        autoClose: false,
      });
      return;
    }

    // Validate required fields
    if (
      !formData.fullName ||
      //!formData.aadhaarVerified ||
      !formData.panVerified ||
      !formData.bankVerified ||
      !formData.selfieImage
    ) {
      setModal({
        show: true,
        type: "error",
        title: "Incomplete Verification",
        message:
          "Please enter your full name, complete Aadhaar verification, PAN verification, Bank verification, and capture selfie before submitting.",
        autoClose: false,
      });
      return;
    }

    setLoading(true);
    try {
      const currentUserKycID = isUpdateMode ? userKycID : uuidv4();
      let liveSelfiePhotoRef =
        formData.selfieImage instanceof File
          ? ""
          : formData.selfieImage
          ? formData.selfieImage.name
          : "";

      if (formData.selfieImage instanceof File) {
        const result = await uploadFileToS3Path(
          formData.selfieImage,
          bucketfolder
        );
        liveSelfiePhotoRef = result.fileName;
      }

      const userKycInput = {
        UserKycID: currentUserKycID,
        EmailID: activeUser,
        IdType: "-",
        IdNumber: "-",
        LiveSelfiePhoto: liveSelfiePhotoRef,
        Status: "Pending",
      };

      if (isUpdateMode) {
        await postQuery({
          target: "updateUserKyc",
          userKycInput: {
            ...userKycInput,
            Status: formData.Status || "Pending",
          },
        });
      } else {
        await postQuery({
          target: "createUserKyc",
          userKycInput: { ...userKycInput, Status: "Pending" },
        });
        setUserKycID(currentUserKycID);
        setIsUpdateMode(true);
      }

      setSubmitted(true);
      setEditMode(false);
      setModal({
        show: true,
        type: "success",
        title: "Success!",
        message: isUpdateMode
          ? "KYC updated successfully."
          : "KYC submitted successfully.",
        autoClose: false,
      });
    } catch (error) {
      console.error("Error submitting KYC:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error!",
        message: "Failed to submit KYC. Please try again.",
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    let bgColor = "",
      textColor = "";
    switch (status) {
      case "Pending":
        bgColor = "#fef3c7";
        textColor = "#b45309";
        break;
      case "Approved":
        bgColor = "#d1fae5";
        textColor = "#065f46";
        break;
      default:
        bgColor = "#f3f4f6";
        textColor = "#374151";
    }
    return (
      <Box
        sx={{
          backgroundColor: bgColor,
          color: textColor,
          padding: "4px 10px",
          borderRadius: "999px",
          fontSize: "0.75rem",
          fontWeight: 500,
          display: "inline-block",
        }}
      >
        {status === "Pending" ? "In Review" : status}
      </Box>
    );
  };

  return (
    <div className="container kyc-verification mt-4">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <div className="card p-2">
        <style>
          {`
            .form-check-label { margin-left: 8px; }
            .form-check-input:checked { background-color: #ff9800; border-color: #ff9800; }
            .form-check-input:focus { border-color: #ff9800; box-shadow: 0 0 0 0.2rem rgba(255, 152, 0, 0.25); }
            .submit-btn-more { background-color: #de6b14; color: white; padding: 10px 20px; border: 1px solid #de6b14; border-radius: 5px; font-size: 12px; cursor: pointer; }
            .submit-btn-more:hover { background-color: #c55a0e; }
            .submit-btn-more:disabled { opacity: 0.6; cursor: not-allowed; }
          `}
        </style>

        {isUpdateMode && formData.Status && (
          <div className="row mb-3">
            <div className="col-12">
              <div
                className="d-flex justify-content-between align-items-center p-2"
                style={{
                  backgroundColor:
                    formData.Status === "Approved" ? "#d1fae573" : "#fef3c750",
                  color: formData.Status === "Approved" ? "#065f46" : "#b45309",
                  borderLeft:
                    formData.Status === "Approved"
                      ? "3px solid #00b658fb"
                      : "3px solid #b69203f7",
                  borderRadius: "5px",
                }}
              >
                <span
                  className="p-1 px-2 rounded"
                  style={{ fontSize: "0.875rem" }}
                >
                  Current Status: {getStatusBadge(formData.Status)}
                </span>
              </div>
            </div>
          </div>
        )}

        <div
          className="table-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: 5,
          }}
        >
          <div className="header-text">
            <div className="head-text">KYC Verification</div>
            <div className="head-subtext" style={{ color: "#666" }}>
              Complete the following steps to verify your identity.
            </div>
          </div>
          <div
            className="submit-btn-form"
            style={{ display: "flex", gap: "10px" }}
          >
            {isUpdateMode &&
            formData.Status &&
            formData.Status !== "Approved" ? (
              !editMode ? (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setEditMode(true)}
                  disabled={loading}
                >
                  Edit
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setEditMode(false);
                      fetchExistingKYC();
                    }}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </>
              )
            ) : !isUpdateMode ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading || submitted}
              >
                {loading ? "Submitting..." : submitted ? "Submitted" : "Submit"}
              </Button>
            ) : null}
          </div>
        </div>

        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ mb: 2, "& .MuiTabs-indicator": { display: "none" } }}
        >
          <Tab
            label="KYC Information"
            sx={{
              borderBottom: "2px solid #de6b14 !important",
              "&:not(.Mui-selected)": { borderBottom: "none", border: "none" },
              "&.Mui-selected": {
                backgroundColor: "#de6b14 !important",
                color: "white",
                borderBottom: "2px solid #de6b14 !important",
                border: "none",
              },
            }}
          />
          <Tab
            label="Remarks"
            disabled={!isUpdateMode || formData.Status === "Approved"}
            sx={{
              borderBottom: "2px solid #de6b14 !important",
              "&:not(.Mui-selected)": { borderBottom: "none", border: "none" },
              "&.Mui-selected": {
                backgroundColor: "#de6b14 !important",
                color: "white",
                borderBottom: "2px solid #de6b14 !important",
                border: "none",
              },
              "&.Mui-disabled": { color: "rgba(0, 0, 0, 0.38)" },
            }}
          />
        </Tabs>

        <div className="mt-4 p-3 border rounded bg-light">
          {activeTab === 0 && (
            <div
              className="form-sec"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                padding: "10px",
              }}
            >
              {/* Name Field */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Full Name *
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", mb: 1, display: "block" }}
                    >
                      Enter your name as per Aadhaar card
                    </Typography>
                    <TextField
                      value={formData.fullName}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          fullName: e.target.value,
                        }))
                      }
                      placeholder="Enter your full name"
                      disabled={readOnly}
                      sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                      size="small"
                    />
                  </FormControl>
                </div>
              </div>

              {/* Aadhaar Verification */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Aadhaar Verification{" "}
                      {formData.aadhaarVerified && (
                        <CheckCircleIcon
                          sx={{ color: "#16a34a", fontSize: 18, ml: 1 }}
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", mb: 1, display: "block" }}
                    >
                      Enter your 12-digit Aadhaar number
                    </Typography>
                    <TextField
                      value={formData.aadhaarNumber}
                      onChange={handleAadhaarNumberChange}
                      placeholder="Enter 12-digit Aadhaar number"
                      disabled={readOnly || formData.aadhaarVerified}
                      inputProps={{ maxLength: 12 }}
                      sx={{
                        backgroundColor:
                          readOnly || formData.aadhaarVerified
                            ? "#f5f5f5"
                            : "white",
                      }}
                      size="small"
                    />
                  </FormControl>
                </div>
                <div
                  className="col-md-6"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    paddingBottom: "4px",
                  }}
                >
                  {formData.aadhaarVerified ? (
                    <span
                      style={{
                        backgroundColor: "#dcfce7",
                        color: "#166534",
                        padding: "6px 16px",
                        borderRadius: "5px",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      Verified
                    </span>
                  ) : (
                    <button
                      className="submit-btn-more"
                      onClick={handleAadhaarModalOpen}
                      disabled={
                        !formData.aadhaarNumber ||
                        formData.aadhaarNumber.length !== 12 ||
                        readOnly
                      }
                      style={{
                        opacity:
                          !formData.aadhaarNumber ||
                          formData.aadhaarNumber.length !== 12 ||
                          readOnly
                            ? 0.6
                            : 1,
                      }}
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>

              {/* PAN Verification */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      PAN Verification{" "}
                      {formData.panVerified && (
                        <CheckCircleIcon
                          sx={{ color: "#16a34a", fontSize: 18, ml: 1 }}
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", mb: 1, display: "block" }}
                    >
                      Enter your PAN number
                    </Typography>
                    <TextField
                      value={formData.panNumber}
                      onChange={handlePanNumberChange}
                      placeholder="Enter PAN number (e.g., ABCDE1234F)"
                      disabled={readOnly || formData.panVerified}
                      inputProps={{ maxLength: 10 }}
                      sx={{
                        backgroundColor:
                          readOnly || formData.panVerified
                            ? "#f5f5f5"
                            : "white",
                      }}
                      size="small"
                    />
                  </FormControl>
                </div>
                <div
                  className="col-md-6"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    paddingBottom: "4px",
                  }}
                >
                  {formData.panVerified ? (
                    <span
                      style={{
                        backgroundColor: "#dcfce7",
                        color: "#166534",
                        padding: "6px 16px",
                        borderRadius: "5px",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      Verified
                    </span>
                  ) : (
                    <button
                      className="submit-btn-more"
                      onClick={handlePanModalOpen}
                      disabled={
                        !formData.panNumber ||
                        formData.panNumber.length !== 10 ||
                        readOnly
                      }
                      style={{
                        opacity:
                          !formData.panNumber ||
                          formData.panNumber.length !== 10 ||
                          readOnly
                            ? 0.6
                            : 1,
                      }}
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>

              {/* Bank Verification */}
              <div className="row mb-3">
                <div className="col-md-4">
                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Bank Verification{" "}
                      {formData.bankVerified && (
                        <CheckCircleIcon
                          sx={{ color: "#16a34a", fontSize: 18, ml: 1 }}
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", mb: 1, display: "block" }}
                    >
                      Bank Account Number
                    </Typography>
                    <TextField
                      value={formData.bankAccountNumber}
                      onChange={handleBankAccountChange}
                      placeholder="Enter Bank Account Number"
                      disabled={readOnly || formData.bankVerified}
                      sx={{
                        backgroundColor:
                          readOnly || formData.bankVerified
                            ? "#f5f5f5"
                            : "white",
                      }}
                      size="small"
                    />
                  </FormControl>
                </div>
                <div className="col-md-4">
                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      &nbsp;
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", mb: 1, display: "block" }}
                    >
                      IFSC Code
                    </Typography>
                    <TextField
                      value={formData.bankIfsc}
                      onChange={handleBankIfscChange}
                      placeholder="Enter IFSC Code"
                      disabled={readOnly || formData.bankVerified}
                      inputProps={{ maxLength: 11 }}
                      sx={{
                        backgroundColor:
                          readOnly || formData.bankVerified
                            ? "#f5f5f5"
                            : "white",
                      }}
                      size="small"
                    />
                  </FormControl>
                </div>
                <div
                  className="col-md-4"
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    paddingBottom: "4px",
                  }}
                >
                  {formData.bankVerified ? (
                    <span
                      style={{
                        backgroundColor: "#dcfce7",
                        color: "#166534",
                        padding: "6px 16px",
                        borderRadius: "5px",
                        fontSize: "13px",
                        fontWeight: 500,
                      }}
                    >
                      Verified
                    </span>
                  ) : (
                    <button
                      className="submit-btn-more"
                      onClick={handleBankModalOpen}
                      disabled={
                        readOnly ||
                        !formData.bankAccountNumber ||
                        formData.bankAccountNumber.length < 9 ||
                        !formData.bankIfsc ||
                        formData.bankIfsc.length !== 11
                      }
                      style={{
                        opacity:
                          readOnly ||
                          !formData.bankAccountNumber ||
                          formData.bankAccountNumber.length < 9 ||
                          !formData.bankIfsc ||
                          formData.bankIfsc.length !== 11
                            ? 0.6
                            : 1,
                      }}
                    >
                      Verify
                    </button>
                  )}
                </div>
              </div>

              {/* Live Photo / Selfie Verification */}
              <div className="row">
                <div className="col-md-6">
                  <FormControl fullWidth>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Live Photo / Selfie Verification *
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{ color: "#666", marginTop: "8px" }}
                    >
                      To match user's selfie with uploaded ID.
                    </Typography>
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => setWebcamDialogOpen(true)}
                      disabled={readOnly}
                      style={{ marginTop: "8px", width: "70%" }}
                    >
                      Capture live selfie photo
                    </Button>
                    <div
                      className="sub-t"
                      style={{
                        fontSize: "12px",
                        color: "#888",
                        marginTop: "4px",
                      }}
                    >
                      Option to "Take a Photo" using device camera (Web)
                    </div>
                  </FormControl>
                </div>

                {capturedImage && (
                  <div
                    className="col-md-6"
                    style={{
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={capturedImage}
                      alt="Captured Selfie"
                      style={{
                        width: "120px",
                        height: "100px",
                        marginTop: "8px",
                        borderRadius: 6,
                        border: "1px solid #ccc",
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: "8px",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={removeSelfie}
                        disabled={readOnly}
                        sx={{ alignSelf: "flex-start" }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                          setFormData((prev) => ({
                            ...prev,
                            selfieImage: null,
                          }));
                          setCapturedImage(null);
                          setWebcamDialogOpen(true);
                        }}
                        size="small"
                        sx={{
                          textTransform: "none",
                          height: "20px",
                          width: "120px",
                        }}
                        disabled={readOnly}
                      >
                        Recapture Selfie
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 1 && isUpdateMode && (
            <Box className="tab-content">
              <div className="p-3">
                <Typography variant="h6" gutterBottom>
                  Remarks
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.remarks}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                  placeholder="Enter any remarks or additional information..."
                  disabled={readOnly}
                  sx={{ backgroundColor: readOnly ? "#f5f5f5" : "white" }}
                />
              </div>
            </Box>
          )}
        </div>

        {/* Webcam Capture Modal */}
        <Dialog
          open={webcamDialogOpen}
          onClose={() => {
            stopWebcam();
            setWebcamDialogOpen(false);
          }}
          aria-labelledby="webcam-dialog-title"
          fullWidth
        >
          <DialogTitle>Capture Selfie</DialogTitle>
          <DialogContent>
            <video
              ref={videoRef}
              autoPlay
              style={{ width: "100%", height: "auto", marginTop: "8px" }}
            />
            <canvas
              ref={canvasRef}
              style={{ display: "none" }}
              width="640"
              height="480"
            />
          </DialogContent>
          <DialogActions>
            <button className="submit-btn-more" onClick={capturePhoto}>
              Capture Photo
            </button>
            <button
              className="submit-btn-more"
              onClick={() => {
                stopWebcam();
                setWebcamDialogOpen(false);
              }}
            >
              Cancel
            </button>
          </DialogActions>
        </Dialog>

        {/* Aadhaar OTP Modal */}
        {aadhaarModalOpen && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "8px",
                width: "450px",
                maxWidth: "90vw",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  marginBottom: "20px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "16px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#1f2937",
                  }}
                >
                  Aadhaar OTP Verification
                </h3>
              </div>
              <div className="modal-body" style={{ marginBottom: "24px" }}>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginBottom: "16px",
                  }}
                >
                  Aadhaar Number: <strong>{formData.aadhaarNumber}</strong>
                </p>
                {!aadhaarOtpSent ? (
                  <p style={{ fontSize: "14px", color: "#666" }}>
                    Click "Send OTP" to receive OTP on your Aadhaar-linked
                    mobile number.
                  </p>
                ) : (
                  <>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#374151",
                      }}
                    >
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      value={aadhaarOtp}
                      onChange={(e) =>
                        setAadhaarOtp(
                          e.target.value.replace(/\D/g, "").slice(0, 6)
                        )
                      }
                      placeholder="Enter 6-digit OTP"
                      maxLength={6}
                      style={{
                        width: "100%",
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: "14px",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </>
                )}
              </div>
              <div
                className="modal-footer"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  onClick={handleAadhaarModalClose}
                  disabled={aadhaarLoading}
                  style={{
                    padding: "10px 20px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    color: "#374151",
                    borderRadius: "5px",
                    cursor: aadhaarLoading ? "not-allowed" : "pointer",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                {!aadhaarOtpSent ? (
                  <button
                    className="submit-btn-more"
                    onClick={handleAadhaarSendOtp}
                    disabled={aadhaarLoading}
                  >
                    {aadhaarLoading ? "Sending..." : "Send OTP"}
                  </button>
                ) : (
                  <button
                    className="submit-btn-more"
                    onClick={handleAadhaarOtpVerify}
                    disabled={aadhaarLoading || aadhaarOtp.length !== 6}
                  >
                    {aadhaarLoading ? "Verifying..." : "Verify OTP"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PAN Verification Modal */}
        {panModalOpen && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "8px",
                width: "450px",
                maxWidth: "90vw",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  marginBottom: "20px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "16px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#1f2937",
                  }}
                >
                  PAN Verification
                </h3>
              </div>
              <div className="modal-body" style={{ marginBottom: "24px" }}>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginBottom: "16px",
                  }}
                >
                  PAN Number: <strong>{formData.panNumber}</strong>
                </p>
                <p style={{ fontSize: "14px", color: "#666" }}>
                  Click "Verify" to verify your PAN details.
                </p>
              </div>
              <div
                className="modal-footer"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  onClick={handlePanModalClose}
                  disabled={panLoading}
                  style={{
                    padding: "10px 20px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    color: "#374151",
                    borderRadius: "5px",
                    cursor: panLoading ? "not-allowed" : "pointer",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                <button
                  className="submit-btn-more"
                  onClick={handlePanVerify}
                  disabled={panLoading}
                >
                  {panLoading ? "Verifying..." : "Verify"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bank Verification Modal */}
        {bankModalOpen && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "white",
                padding: "24px",
                borderRadius: "8px",
                width: "450px",
                maxWidth: "90vw",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                className="modal-header"
                style={{
                  marginBottom: "20px",
                  borderBottom: "1px solid #e5e7eb",
                  paddingBottom: "16px",
                }}
              >
                <h3
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: "600",
                    color: "#1f2937",
                  }}
                >
                  Bank Account Verification
                </h3>
              </div>
              <div className="modal-body" style={{ marginBottom: "24px" }}>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginBottom: "16px",
                  }}
                >
                  Account Number: <strong>{formData.bankAccountNumber}</strong>
                </p>
                <p
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    marginBottom: "16px",
                  }}
                >
                  IFSC Code: <strong>{formData.bankIfsc}</strong>
                </p>
                <p style={{ fontSize: "14px", color: "#666" }}>
                  Click "Verify" to verify your bank account details.
                </p>
              </div>
              <div
                className="modal-footer"
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "12px",
                }}
              >
                <button
                  onClick={handleBankModalClose}
                  disabled={bankLoading}
                  style={{
                    padding: "10px 20px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    color: "#374151",
                    borderRadius: "5px",
                    cursor: bankLoading ? "not-allowed" : "pointer",
                    fontSize: "14px",
                  }}
                >
                  Cancel
                </button>
                <button
                  className="submit-btn-more"
                  onClick={handleBankVerify}
                  disabled={bankLoading}
                >
                  {bankLoading ? "Verifying..." : "Verify"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SellerFarmerKYC;
